import { window } from './utils/browser';
import { EditorMode, IntentType } from './constants';
import {
  getPayloadFromToken,
  redactTokenPayload,
  redactTokenSignature,
  TokenInfo,
  TokenPayload,
} from './token';

export enum TokenErrorType {
  Invalid = 'Invalid',
  Missing = 'Missing',
  Expired = 'Expired',
  Unknown = 'Unknown',
}

export class TokenError extends Error {
  type: TokenErrorType;
  tokenInfo?: TokenInfo;

  constructor(type: TokenErrorType, tokenInfo?: TokenInfo) {
    super(`TokenError: ${type} token`);

    // See: https://github.com/Microsoft/TypeScript-wiki/blob/master/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, TokenError.prototype);

    this.name = 'TokenError';
    this.type = type;
    this.tokenInfo = tokenInfo;
  }
}

export function isTokenExpired(payload: TokenPayload) {
  return payload.exp * 1000 - new Date().getTime() <= 0;
}

export function extractTokenInfoFromJwt(jwt: string) {
  try {
    const payload = getPayloadFromToken(jwt) as unknown;
    return payload as TokenPayload;
  } catch (e) {
    throw new TokenError(TokenErrorType.Invalid);
  }
}

function getTokenFromUrlSearchParams() {
  return new URLSearchParams(window?.location.search).get('token');
}

function getTokenFromUrlHash() {
  const hash = window?.location.hash;

  if (hash && hash.indexOf('token=') !== -1) {
    const hashMatch = hash.match(/token=(.*)$/);
    return hashMatch && hashMatch.length > 1 ? hashMatch[1] : null;
  }

  return null;
}

/**
 * The document editor (both embedded and standalone), pull their
 * JWT from the URL query string. This function attempts to retrieve and
 * validate the token in the query string. The token used to be stored in
 * the URL hash, so for backwards compatibility this will look there if it
 * is not found in the query string.
 *
 * If the token is either missing, invalid, or expired, a `TokenError`
 * will be thrown, containing details about the error type.
 */
export function getTokenFromUrl(): TokenInfo {
  const token = getTokenFromUrlSearchParams() || getTokenFromUrlHash();

  if (!token) {
    throw new TokenError(TokenErrorType.Missing);
  }

  const payload = getPayloadFromToken(token);

  if (!payload) {
    throw new TokenError(TokenErrorType.Invalid);
  }

  const redactedToken = redactTokenSignature(token);
  const redactedPayload = redactTokenPayload(payload);

  return { token, redactedToken, payload, redactedPayload };
}

export function validateToken(tokenInfo: TokenInfo) {
  const { payload } = tokenInfo;
  if (!payload || !payload.zdocs || payload.iss !== 'zdocs/auth') {
    throw new TokenError(TokenErrorType.Invalid);
  }

  const currentDate = new Date().getTime();
  const expirationDate = payload.exp * 1000;
  const remainingTime = expirationDate - currentDate;

  if (remainingTime <= 0) {
    throw new TokenError(TokenErrorType.Expired, tokenInfo);
  }
}

export function getDocumentMode(payload: TokenPayload): EditorMode {
  switch (payload.zdocs.intent) {
    case IntentType.READ:
    case IntentType.VIEW: {
      return EditorMode.Read;
    }

    case IntentType.FILL:
    case IntentType.SIGN: {
      return EditorMode.Fill;
    }

    case IntentType.EDIT:
    case IntentType.COMPOSE:
    case IntentType.ANNOTATE:
    case IntentType.SELF_SERVE: {
      return EditorMode.Edit;
    }

    default: {
      const [document] = payload.zdocs.documents || [];
      return document?.mode ?? EditorMode.Read;
    }
  }
}
